import { getYearFromDate } from '../utility/dateUtils';
import {
  isEmpty,
  forbiddenCharacters,
  validateLength,
} from '../utility/validation';
import { FIELD_TYPES } from './fieldTypes';

const TODAY = new Date();
export const CURRENT_YEAR = getYearFromDate(TODAY);
export const CURRENT_MONTH = TODAY.getMonth();

export const YEAR_FORMAT = 'yyyy';
export const MONTH_FORMAT = 'MMMM';

export const NEW_EDUCATION_VALUES = {
  school: '',
  degree: '',
  start_year: CURRENT_YEAR,
  start_month: null,
  end_year: null,
  end_month: null,
};

export const NEW_EXPERIENCE_VALUES = {
  company: '',
  job_title: '',
  start_year: CURRENT_YEAR,
  start_month: null,
  end_year: null,
  end_month: null,
};

const getMaxDateForStartYear = values => {
  const year = values.end_year || CURRENT_YEAR;

  return new Date(year, 11, 31);
};

const validateMaxYear = (value, values) => {
  return !(
    (isEmpty(values.end_year) && value > values.end_year) ||
    value > CURRENT_YEAR
  );
};

const validateStartMonth = (value, values) => {
  if (!value) return true;
  return !(
    isEmpty(values.end_month) &&
    values.start_year === values.end_year &&
    value > values.end_month
  );
};

const getMinDateForEndYear = values => {
  return new Date(values.start_year, 0, 1);
};

const getMaxDateForEndYear = () => {
  return new Date(CURRENT_YEAR, CURRENT_MONTH, 20);
};

const validateInvalidDateEndYear = (value, values) => {
  if (isEmpty(value)) {
    return !(value < values.start_year || value > CURRENT_YEAR);
  }
  return !(value && value > CURRENT_YEAR);
};

const validateInvalidDateEndMonth = (value, values) => {
  if (!value) return true;
  if (values.end_year === values.start_year)
    return !(value < values.start_month);
  return true;
};

const validateMaxMonth = (value, year) => {
  if (year === CURRENT_YEAR) return !(value > CURRENT_MONTH + 1);
  return true;
};

export const EDUCATION_FIELDS = [
  {
    name: 'start_year',
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'startYear',
    required: true,
    valueFormat: YEAR_FORMAT,
    getMaxDate: getMaxDateForStartYear,
    validators: [
      {
        validator: (value, values) => validateMaxYear(value, values),
        shouldPassFormValues: true,
        type: 'invalidStartDate',
      },
    ],
  },
  {
    name: 'start_month',
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'startMonth',
    required: true,
    valueFormat: MONTH_FORMAT,
    validators: [
      {
        validator: (value, values) =>
          validateMaxMonth(value, values.start_year),
        shouldPassFormValues: true,
        type: 'invalidMonth',
      },
      {
        validator: (value, values) => validateStartMonth(value, values),
        shouldPassFormValues: true,
        type: 'invalidStartDate',
      },
    ],
  },
  {
    name: 'end_year',
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'endYear',
    required: true,
    valueFormat: YEAR_FORMAT,
    getMinDate: values => getMinDateForEndYear(values),
    getMaxDate: () => getMaxDateForEndYear(),
    validators: [
      {
        validator: (value, values) => !(values.end_month && !value),
        type: 'required',
        shouldPassFormValues: true,
      },
      {
        validator: (value, values) => validateInvalidDateEndYear(value, values),
        shouldPassFormValues: true,
        type: 'invalidEndDate',
      },
    ],
  },
  {
    name: 'end_month',
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'endMonth',
    required: true,
    valueFormat: MONTH_FORMAT,
    validators: [
      {
        validator: (value, values) => validateMaxMonth(value, values.end_year),
        shouldPassFormValues: true,
        type: 'invalidMonth',
      },
      {
        validator: (value, values) =>
          validateInvalidDateEndMonth(value, values),
        shouldPassFormValues: true,
        type: 'invalidEndDate',
      },
    ],
  },
  {
    name: 'school',
    required: true,
    translationKey: 'school',
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: value => validateLength(value, 0, 255),
        type: 'maxLength',
      },
    ],
  },
  {
    name: 'degree',
    required: true,
    translationKey: 'degree',
    validators: [
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: value => validateLength(value, 0, 255),
        type: 'maxLength',
      },
    ],
  },
];

export const EXPERIENCE_FIELDS = [
  {
    name: 'start_year',
    type: FIELD_TYPES.DATE_PICKER,
    required: true,
    translationKey: 'startYear',
    valueFormat: YEAR_FORMAT,
    getMaxDate: getMaxDateForStartYear,
    validators: [
      {
        validator: (value, values) => validateMaxYear(value, values),
        shouldPassFormValues: true,
        type: 'invalidStartDate',
      },
    ],
  },

  {
    name: 'start_month',
    type: FIELD_TYPES.DATE_PICKER,
    required: true,
    translationKey: 'startMonth',
    valueFormat: MONTH_FORMAT,
    validators: [
      {
        validator: (value, values) =>
          validateMaxMonth(value, values.start_year),
        shouldPassFormValues: true,
        type: 'invalidMonth',
      },
      {
        validator: (value, values) => validateStartMonth(value, values),
        shouldPassFormValues: true,
        type: 'invalidStartDate',
      },
    ],
  },
  {
    name: 'end_year',
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'endYear',
    required: true,
    valueFormat: YEAR_FORMAT,
    getMinDate: getMinDateForEndYear,
    getMaxDate: getMaxDateForEndYear,
    validators: [
      {
        validator: (value, values) => !(values.end_month && !value),
        type: 'required',
        shouldPassFormValues: true,
      },
      {
        validator: (value, values) => validateInvalidDateEndYear(value, values),
        shouldPassFormValues: true,
        type: 'invalidEndDate',
      },
    ],
  },
  {
    name: 'end_month',
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'endMonth',
    required: true,
    valueFormat: MONTH_FORMAT,
    validators: [
      {
        validator: (value, values) => validateMaxMonth(value, values.end_year),
        shouldPassFormValues: true,
        type: 'invalidMonth',
      },
      {
        validator: (value, values) =>
          validateInvalidDateEndMonth(value, values),
        shouldPassFormValues: true,
        type: 'invalidEndDate',
      },
    ],
  },
  {
    name: 'company',
    required: true,
    translationKey: 'company',
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: value => validateLength(value, 0, 255),
        type: 'maxLength',
      },
    ],
  },
  {
    name: 'job_title',
    required: true,
    translationKey: 'jobTitle',
    validators: [
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: value => validateLength(value, 0, 255),
        type: 'maxLength',
      },
    ],
  },
];
